import type {
  ConsentCategories,
  ConsentCookie,
  ConsentMachineState,
} from './types';

export type ConsentInitInput = {
  cookie: ConsentCookie | null;
  gpcActive: boolean;
  hostname: string;
};

export type ConsentInitOutput = {
  machineState: ConsentMachineState;
  categories: ConsentCategories;
  gpc: boolean;
  origin: string;
  cookieToWrite: ConsentCookie | null;
  // True when this load already has a consent decision to replay to Google's
  // tags — equivalently, when either an existing cookie was read or this call
  // minted one (`cookieToWrite`). It is the predicate for the bootstrap
  // `consent update`: replaying an all-denied state for a visitor who has not
  // decided yet would overwrite every region-scoped `consent default` in the
  // same dataLayer batch. `gpc-auto-rejected` counts as a decision — the
  // browser made it and this call is writing the cookie for it. It lives here
  // rather than being re-derived from `machineState` at the call site so the
  // predicate has exactly one home.
  hasStoredDecision: boolean;
};

const NECESSARY_ONLY: ConsentCategories = {
  necessary: true,
  analytics: false,
  advertising: false,
};

export function computeInitialState(
  input: ConsentInitInput,
): ConsentInitOutput {
  if (input.cookie) {
    const categories: ConsentCategories = input.gpcActive
      ? { ...input.cookie.categories, advertising: false }
      : input.cookie.categories;
    return {
      machineState: 'consented',
      categories,
      gpc: input.gpcActive,
      origin: input.cookie.origin,
      cookieToWrite: null,
      hasStoredDecision: true,
    };
  }
  // GPC is a sale/share opt-out, not an analytics opt-out (2026-09-16). A GPC
  // visitor with no cookie is therefore an ordinary undecided visitor whose
  // Advertising category is locked off: the machine lands on `banner-shown`,
  // no cookie is minted, and `hasStoredDecision` stays false so the bootstrap
  // pushes no replay and the region-resolved analytics default survives.
  // GoogleTagManager's partial GPC_AD_DENIAL_SIGNALS update, pushed before
  // gtm.start, is what denies the three ad_* signals for this load. Until
  // 2026-09-16 this branch minted an all-denied `gpc-auto-rejected` cookie,
  // which cost every Brave / Firefox-private visitor their analytics session
  // and never showed them a banner.
  return {
    machineState: 'banner-shown',
    categories: NECESSARY_ONLY,
    gpc: input.gpcActive,
    origin: input.hostname,
    cookieToWrite: null,
    hasStoredDecision: false,
  };
}

export function buildSavePayload(
  categories: ConsentCategories,
  gpc: boolean,
  hostname: string,
  version: string,
): ConsentCookie {
  return {
    categories: {
      necessary: true,
      analytics: categories.analytics === true,
      advertising: categories.advertising === true,
    },
    version,
    gpc,
    origin: hostname,
  };
}

// The first-visit surface a visitor gets, from the categories the region seeded
// (GPC lock already applied). Three-way because the seeded state is three-way:
//   both on            -> 'none'   (every US subdivision, the global
//                                   fallback): nothing to ask; the footer's
//                                   Cookie Preferences link is the opt-out.
//   analytics on only  -> 'notice' (the UK, any GPC visitor in a granting
//                                   region): DUAA 2025's
//                                   statistical-cookies exception needs clear
//                                   information and a working opt-out, and a
//                                   slim dismissible notice with a Cookie
//                                   Preferences link is exactly that.
//   analytics off      -> 'banner' (the EU/EEA): prior consent is required, so
//                                   the full equal-prominence banner stays.
// ConsentProvider adds the two states this function cannot know about:
// 'pending' while the defaults have not settled, and 'banner' for a settled
// visitor whose region never resolved.
export type ConsentSurface = 'pending' | 'banner' | 'notice' | 'none';

export function surfaceForSeededCategories(
  categories: ConsentCategories,
): Exclude<ConsentSurface, 'pending'> {
  if (!categories.analytics) return 'banner';
  return categories.advertising ? 'none' : 'notice';
}
